import type { BotLevel } from "../constants";
import type { Rng } from "../rng";
import { pick, shuffle } from "../rng";
import { GameError, type GameEvent, type TurnGameModule, type Winner } from "./types";

/**
 * EduPoly – a property-trading board game following the classic rules (40 squares, 2–4 players, dice, doubles,
 * jail, buying, rent, colour sets, houses/hotels with even building, mortgages, auctions, trading, Chance /
 * Community-Chest cards, taxes, bankruptcy). Names, texts and board art are our own.
 *
 * Learning twist (the site's intention): the important decisions are earned with a question –
 *  - buying a property (wrong answer -> it goes to auction instead),
 *  - paying rent or tax (right answer -> only HALF is paid),
 *  - building a house/hotel (wrong answer -> nothing is built, nothing is paid),
 *  - getting out of jail for free (wrong answer -> stay, roll for doubles or pay).
 * Everything else (dice, cards, auctions, trades, mortgages) works exactly like the board game.
 */

export const START_CASH = 1500;
export const GO_SALARY = 200;
export const JAIL_FINE = 50;
export const MAX_HOUSES = 32;
export const MAX_HOTELS = 12;

export type Group = "brown" | "lblue" | "pink" | "orange" | "red" | "yellow" | "green" | "dblue";
export type Sq =
  | { t: "go"; name: string }
  | { t: "prop"; name: string; group: Group; price: number; rent: number[]; house: number }
  | { t: "rail"; name: string; price: number }
  | { t: "util"; name: string; price: number }
  | { t: "chest"; name: string }
  | { t: "chance"; name: string }
  | { t: "tax"; name: string; amount: number }
  | { t: "jail"; name: string }
  | { t: "parking"; name: string }
  | { t: "gojail"; name: string };

const P = (name: string, group: Group, price: number, rent: number[], house: number): Sq => ({ t: "prop", name, group, price, rent, house });
const R = (name: string): Sq => ({ t: "rail", name, price: 200 });
const U = (name: string): Sq => ({ t: "util", name, price: 150 });

export const SQUARES: Sq[] = [
  { t: "go", name: "START" },
  P("Æblevej", "brown", 60, [2, 10, 30, 90, 160, 250], 50),
  { t: "chest", name: "Fælleskassen" },
  P("Pærevej", "brown", 60, [4, 20, 60, 180, 320, 450], 50),
  { t: "tax", name: "Indkomstskat", amount: 200 },
  R("Nordbanen"),
  P("Havnegade", "lblue", 100, [6, 30, 90, 270, 400, 550], 50),
  { t: "chance", name: "Chancen" },
  P("Fiskertorvet", "lblue", 100, [6, 30, 90, 270, 400, 550], 50),
  P("Strandvejen", "lblue", 120, [8, 40, 100, 300, 450, 600], 50),
  { t: "jail", name: "Fængsel" },
  P("Rosenvej", "pink", 140, [10, 50, 150, 450, 625, 750], 100),
  U("Elværket"),
  P("Tulipanstræde", "pink", 140, [10, 50, 150, 450, 625, 750], 100),
  P("Lilievej", "pink", 160, [12, 60, 180, 500, 700, 900], 100),
  R("Østbanen"),
  P("Skolegade", "orange", 180, [14, 70, 200, 550, 750, 950], 100),
  { t: "chest", name: "Fælleskassen" },
  P("Bogtorvet", "orange", 180, [14, 70, 200, 550, 750, 950], 100),
  P("Kridtvej", "orange", 200, [16, 80, 220, 600, 800, 1000], 100),
  { t: "parking", name: "Fri parkering" },
  P("Slotsgade", "red", 220, [18, 90, 250, 700, 875, 1050], 150),
  { t: "chance", name: "Chancen" },
  P("Kongens Plads", "red", 220, [18, 90, 250, 700, 875, 1050], 150),
  P("Dronningvej", "red", 240, [20, 100, 300, 750, 925, 1100], 150),
  R("Sydbanen"),
  P("Teatergade", "yellow", 260, [22, 110, 330, 800, 975, 1150], 150),
  P("Museumsgade", "yellow", 260, [22, 110, 330, 800, 975, 1150], 150),
  U("Vandværket"),
  P("Koncertvej", "yellow", 280, [24, 120, 360, 850, 1025, 1200], 150),
  { t: "gojail", name: "Gå i fængsel" },
  P("Parkvej", "green", 300, [26, 130, 390, 900, 1100, 1275], 200),
  P("Skovallé", "green", 300, [26, 130, 390, 900, 1100, 1275], 200),
  { t: "chest", name: "Fælleskassen" },
  P("Eventyrvej", "green", 320, [28, 150, 450, 1000, 1200, 1400], 200),
  R("Vestbanen"),
  { t: "chance", name: "Chancen" },
  P("Rådhuspladsen", "dblue", 350, [35, 175, 500, 1100, 1300, 1500], 200),
  { t: "tax", name: "Luksusskat", amount: 100 },
  P("Kongeslottet", "dblue", 400, [50, 200, 600, 1400, 1700, 2000], 200),
];

export const GROUPS: Record<Group, number[]> = { brown: [], lblue: [], pink: [], orange: [], red: [], yellow: [], green: [], dblue: [] };
SQUARES.forEach((q, i) => { if (q.t === "prop") GROUPS[q.group].push(i); });
export const RAILS = SQUARES.flatMap((q, i) => (q.t === "rail" ? [i] : []));
export const UTILS = SQUARES.flatMap((q, i) => (q.t === "util" ? [i] : []));
export const GROUP_NAME: Record<Group, string> = { brown: "Brun", lblue: "Lyseblå", pink: "Pink", orange: "Orange", red: "Rød", yellow: "Gul", green: "Grøn", dblue: "Mørkeblå" };
export const isBuyable = (i: number) => ["prop", "rail", "util"].includes(SQUARES[i]!.t);
export const priceOf = (i: number): number => { const q = SQUARES[i]!; return q.t === "prop" || q.t === "rail" || q.t === "util" ? q.price : 0; };

// ───────────── cards (own wording, classic effects) ─────────────
type CardFx =
  | { k: "goto"; to: number }
  | { k: "gain"; n: number }
  | { k: "pay"; n: number }
  | { k: "back"; n: number }
  | { k: "jail" }
  | { k: "free" }
  | { k: "nearRail" }
  | { k: "nearUtil" }
  | { k: "repairs"; house: number; hotel: number }
  | { k: "eachPay"; n: number }
  | { k: "eachGet"; n: number };
export interface Card { text: string; fx: CardFx }
export const CHANCE: Card[] = [
  { text: "Ryk frem til START og modtag 200.", fx: { k: "goto", to: 0 } },
  { text: "Ryk frem til Dronningvej. Går du forbi START, får du 200.", fx: { k: "goto", to: 24 } },
  { text: "Ryk frem til Rosenvej. Går du forbi START, får du 200.", fx: { k: "goto", to: 11 } },
  { text: "Ryk frem til nærmeste værk. Ejes det, betaler du 10 gange terningerne.", fx: { k: "nearUtil" } },
  { text: "Ryk frem til nærmeste bane. Ejes den, betaler du dobbelt leje.", fx: { k: "nearRail" } },
  { text: "Ryk frem til nærmeste bane. Ejes den, betaler du dobbelt leje.", fx: { k: "nearRail" } },
  { text: "Banken udbetaler 50 i udbytte.", fx: { k: "gain", n: 50 } },
  { text: "Du slipper gratis ud af fængsel. Behold kortet, til du skal bruge det.", fx: { k: "free" } },
  { text: "Gå tre felter tilbage.", fx: { k: "back", n: 3 } },
  { text: "Gå direkte i fængsel. Du går ikke forbi START.", fx: { k: "jail" } },
  { text: "Reparér dine bygninger: 25 pr. hus og 100 pr. hotel.", fx: { k: "repairs", house: 25, hotel: 100 } },
  { text: "Betal 15 i bøde for for hurtig cykling.", fx: { k: "pay", n: 15 } },
  { text: "Tag en tur med Nordbanen. Går du forbi START, får du 200.", fx: { k: "goto", to: 5 } },
  { text: "Ryk frem til Kongeslottet.", fx: { k: "goto", to: 39 } },
  { text: "Du er valgt til formand. Betal 50 til hver spiller.", fx: { k: "eachPay", n: 50 } },
  { text: "Dit byggelån udbetales: modtag 150.", fx: { k: "gain", n: 150 } },
];
export const CHEST: Card[] = [
  { text: "Ryk frem til START og modtag 200.", fx: { k: "goto", to: 0 } },
  { text: "Bankfejl til din fordel: modtag 200.", fx: { k: "gain", n: 200 } },
  { text: "Lægeregning: betal 50.", fx: { k: "pay", n: 50 } },
  { text: "Du sælger aktier: modtag 50.", fx: { k: "gain", n: 50 } },
  { text: "Du slipper gratis ud af fængsel. Behold kortet, til du skal bruge det.", fx: { k: "free" } },
  { text: "Gå direkte i fængsel. Du går ikke forbi START.", fx: { k: "jail" } },
  { text: "Feriekassen udbetales: modtag 100.", fx: { k: "gain", n: 100 } },
  { text: "Skattetilbagebetaling: modtag 20.", fx: { k: "gain", n: 20 } },
  { text: "Det er din fødselsdag! Modtag 10 fra hver spiller.", fx: { k: "eachGet", n: 10 } },
  { text: "Livsforsikring udbetales: modtag 100.", fx: { k: "gain", n: 100 } },
  { text: "Betal hospitalsregning: 100.", fx: { k: "pay", n: 100 } },
  { text: "Betal skolepenge: 50.", fx: { k: "pay", n: 50 } },
  { text: "Du får honorar for rådgivning: modtag 25.", fx: { k: "gain", n: 25 } },
  { text: "Gaderenovering: 40 pr. hus og 115 pr. hotel.", fx: { k: "repairs", house: 40, hotel: 115 } },
  { text: "Du vinder 2. præmie i en skønhedskonkurrence: modtag 10.", fx: { k: "gain", n: 10 } },
  { text: "Du arver 100.", fx: { k: "gain", n: 100 } },
];

// ───────────── state ─────────────
export interface MPlayer {
  cash: number;
  pos: number;
  jail: number; // 0 = free, 1..3 = failed attempts + 1 while in jail
  jailCards: number;
  out: boolean;
}
export type QKind = "buy" | "rent" | "tax" | "build" | "jail";
export interface MQuestion { kind: QKind; seat: number; sq: number; amount?: number; to?: number | null }
export interface Auction { sq: number; bid: number; bidder: number | null; order: number[]; active: number[]; cur: number }
export interface TradeOffer { from: number; to: number; giveProps: number[]; giveCash: number; getProps: number[]; getCash: number }
export interface Debt { seat: number; items: { to: number | null; amount: number }[] }
export type Stage = "roll" | "buy" | "manage" | "debt" | "auction" | "trade";
export interface FeedItem { seat: number | null; text: string }

export interface MState {
  players: MPlayer[];
  owner: (number | null)[];
  houses: number[]; // 0-4 houses, 5 = hotel
  mort: boolean[];
  turn: number;
  stage: Stage;
  dice: [number, number];
  rolled: boolean;
  doubles: number;
  q: MQuestion | null;
  auction: Auction | null;
  trade: (TradeOffer & { back: Stage }) | null;
  debt: Debt | null;
  pendingMove: number | null;
  buildFailed: boolean;
  jailQuiz: boolean;
  chanceDeck: number[];
  chestDeck: number[];
  lastCard: { deck: "chance" | "chest"; text: string; seat: number } | null;
  feed: FeedItem[];
  turnCount: number;
  limitTurns: number;
  winner: Winner;
}

export type MAction =
  | { type: "roll" }
  | { type: "buy" }
  | { type: "decline" }
  | { type: "build"; sq: number }
  | { type: "sell"; sq: number }
  | { type: "mortgage"; sq: number }
  | { type: "unmortgage"; sq: number }
  | { type: "end" }
  | { type: "payjail" }
  | { type: "usecard" }
  | { type: "jailquiz" }
  | { type: "paydebt" }
  | { type: "bankrupt" }
  | { type: "bid"; amount: number }
  | { type: "pass" }
  | { type: "propose"; to: number; giveProps: number[]; giveCash: number; getProps: number[]; getCash: number }
  | { type: "accept" }
  | { type: "reject" };

const alive = (s: Pick<MState, "players">) => s.players.map((p, i) => (p.out ? -1 : i)).filter((i) => i >= 0);
const say = (s: MState, seat: number | null, text: string) => { s.feed.push({ seat, text }); if (s.feed.length > 16) s.feed.shift(); };

// ───────────── helpers ─────────────
export const ownsAll = (s: { owner: (number | null)[] }, seat: number, g: Group) => GROUPS[g].every((i) => s.owner[i] === seat);
const groupOf = (i: number): Group | null => { const q = SQUARES[i]!; return q.t === "prop" ? q.group : null; };
export const countOwned = (s: { owner: (number | null)[] }, seat: number, idxs: number[]) => idxs.filter((i) => s.owner[i] === seat).length;

export function rentFor(s: MState, sq: number, diceTotal: number): number {
  const q = SQUARES[sq]!;
  const o = s.owner[sq];
  if (o === null || o === undefined || s.mort[sq]) return 0;
  if (q.t === "prop") {
    const h = s.houses[sq]!;
    if (h === 0) return ownsAll(s, o, q.group) ? q.rent[0]! * 2 : q.rent[0]!;
    return q.rent[h]!;
  }
  if (q.t === "rail") return 25 * 2 ** (countOwned(s, o, RAILS) - 1);
  if (q.t === "util") return (countOwned(s, o, UTILS) === 2 ? 10 : 4) * diceTotal;
  return 0;
}

export function netWorth(s: Props, seat: number): number {
  let w = s.players[seat]!.cash;
  s.owner.forEach((o, i) => {
    if (o !== seat) return;
    const q = SQUARES[i]!;
    w += s.mort[i] ? priceOf(i) / 2 : priceOf(i);
    if (q.t === "prop") w += s.houses[i]! * q.house;
  });
  return w;
}
const buildingCount = (s: Props, seat: number) => {
  let h = 0, ho = 0;
  s.owner.forEach((o, i) => { if (o === seat) { const n = s.houses[i]!; if (n === 5) ho++; else h += n; } });
  return { h, ho };
};

/** What may `seat` do with its properties right now? (used by the server to validate AND by bots/UI to enable buttons) */
export const houseSupplyLeft = (s: { houses: number[] }) => MAX_HOUSES - s.houses.reduce((a, h) => a + (h > 0 && h < 5 ? h : 0), 0);
export const hotelSupplyLeft = (s: { houses: number[] }) => MAX_HOTELS - s.houses.filter((h) => h === 5).length;

type Props = Pick<MState, "players" | "owner" | "houses" | "mort"> & { buildFailed?: boolean };
export function manageOptions(s: Props, seat: number) {
  const build: number[] = [], sell: number[] = [], mortgage: number[] = [], unmortgage: number[] = [];
  const me = s.players[seat]!;
  s.owner.forEach((o, i) => {
    if (o !== seat) return;
    const q = SQUARES[i]!;
    if (q.t === "prop") {
      const g = GROUPS[q.group];
      const full = ownsAll(s, seat, q.group) && g.every((x) => !s.mort[x]);
      const h = s.houses[i]!;
      if (!s.buildFailed && full && h < 5 && me.cash >= q.house && g.every((x) => s.houses[x]! >= h) && (h === 4 ? hotelSupplyLeft(s) > 0 : houseSupplyLeft(s) > 0)) build.push(i);
      if (h > 0 && g.every((x) => s.houses[x]! <= h) && (h < 5 || houseSupplyLeft(s) >= 4)) sell.push(i);
    }
    const anyBuild = q.t === "prop" && GROUPS[q.group].some((x) => s.houses[x]! > 0);
    if (!s.mort[i] && !anyBuild) mortgage.push(i);
    if (s.mort[i] && me.cash >= Math.ceil(priceOf(i) / 2 * 1.1)) unmortgage.push(i);
  });
  return { build, sell, mortgage, unmortgage };
}

// ───────────── engine ─────────────
const roll6 = (rng: Rng) => 1 + Math.floor(rng() * 6);
const nameOfSq = (i: number) => SQUARES[i]!.name;

export function actorOf(s: MState): number {
  if (s.q) return s.q.seat;
  if (s.stage === "auction" && s.auction) return s.auction.cur;
  if (s.stage === "trade" && s.trade) return s.trade.to;
  return s.turn;
}

function gain(s: MState, seat: number, n: number) { s.players[seat]!.cash += n; }

/** Pay `amount` to `to` (null = bank). When the player cannot pay, a debt is created and they must raise money or go bankrupt. */
function charge(s: MState, seat: number, amount: number, to: number | null) {
  if (amount <= 0) return;
  const p = s.players[seat]!;
  const owed = (s.debt?.items ?? []).reduce((a, i) => a + i.amount, 0);
  if (!s.debt && p.cash >= amount) {
    p.cash -= amount;
    if (to !== null) s.players[to]!.cash += amount;
    return;
  }
  void owed;
  s.debt ??= { seat, items: [] };
  s.debt.items.push({ to, amount });
  s.stage = "debt";
}

function sendToJail(s: MState, seat: number) {
  const p = s.players[seat]!;
  p.pos = 10;
  p.jail = 1;
  s.doubles = 0;
  s.jailQuiz = false;
  say(s, seat, "kommer i fængsel.");
}

function passGo(s: MState, seat: number) {
  gain(s, seat, GO_SALARY);
  say(s, seat, `passerer START og modtager ${GO_SALARY}.`);
}

function nextIdx(from: number, list: number[]) {
  return list.find((i) => i > from) ?? list[0]!;
}

function land(s: MState, seat: number, rng: Rng, o: { rentMult?: number; utilTen?: boolean } = {}) {
  const p = s.players[seat]!;
  const sq = SQUARES[p.pos]!;
  s.stage = "manage";
  switch (sq.t) {
    case "prop":
    case "rail":
    case "util": {
      const owner = s.owner[p.pos];
      if (owner === null || owner === undefined) {
        s.q = { kind: "buy", seat, sq: p.pos };
        s.stage = "buy";
      } else if (owner !== seat && !s.mort[p.pos]) {
        let amount: number;
        if (o.utilTen) amount = 10 * (roll6(rng) + roll6(rng));
        else amount = rentFor(s, p.pos, s.dice[0] + s.dice[1]) * (o.rentMult ?? 1);
        s.q = { kind: "rent", seat, sq: p.pos, amount, to: owner };
      }
      return;
    }
    case "tax":
      s.q = { kind: "tax", seat, sq: p.pos, amount: sq.amount, to: null };
      return;
    case "chance":
    case "chest":
      drawCard(s, seat, sq.t, rng);
      return;
    case "gojail":
      sendToJail(s, seat);
      return;
    default:
      return;
  }
}

function moveTo(s: MState, seat: number, to: number, collect: boolean) {
  const p = s.players[seat]!;
  if (collect && to < p.pos) passGo(s, seat);
  p.pos = to;
}

function drawCard(s: MState, seat: number, deckName: "chance" | "chest", rng: Rng) {
  const deck = deckName === "chance" ? s.chanceDeck : s.chestDeck;
  const cards = deckName === "chance" ? CHANCE : CHEST;
  const id = deck.shift()!;
  const card = cards[id]!;
  s.lastCard = { deck: deckName, text: card.text, seat };
  say(s, seat, `trækker et ${deckName === "chance" ? "chancekort" : "fælleskassekort"}: ${card.text}`);
  const p = s.players[seat]!;
  const fx = card.fx;
  if (fx.k === "free") {
    p.jailCards++;
    return; // the card is held, not returned to the deck
  }
  deck.push(id);
  switch (fx.k) {
    case "goto":
      moveTo(s, seat, fx.to, true);
      land(s, seat, rng);
      break;
    case "gain":
      gain(s, seat, fx.n);
      break;
    case "pay":
      charge(s, seat, fx.n, null);
      break;
    case "back":
      p.pos = (p.pos - fx.n + 40) % 40;
      land(s, seat, rng);
      break;
    case "jail":
      sendToJail(s, seat);
      break;
    case "nearRail":
      moveTo(s, seat, nextIdx(p.pos, RAILS), true);
      land(s, seat, rng, { rentMult: 2 });
      break;
    case "nearUtil":
      moveTo(s, seat, nextIdx(p.pos, UTILS), true);
      land(s, seat, rng, { utilTen: true });
      break;
    case "repairs": {
      const b = buildingCount(s, seat);
      charge(s, seat, b.h * fx.house + b.ho * fx.hotel, null);
      break;
    }
    case "eachPay":
      for (const o of alive(s)) if (o !== seat) charge(s, seat, fx.n, o);
      break;
    case "eachGet":
      for (const o of alive(s)) if (o !== seat) { const n = Math.min(fx.n, s.players[o]!.cash); s.players[o]!.cash -= n; gain(s, seat, n); }
      break;
  }
}

function startAuction(s: MState, sq: number, afterSeat: number) {
  const order = alive(s);
  const start = order.findIndex((x) => x > afterSeat);
  const active = start < 0 ? order : [...order.slice(start), ...order.slice(0, start)];
  s.auction = { sq, bid: 0, bidder: null, order: active, active: [...active], cur: active[0]! };
  s.stage = "auction";
  say(s, null, `${nameOfSq(sq)} sendes på auktion.`);
}

function advanceAuction(s: MState, from: number) {
  const a = s.auction!;
  if (a.bidder !== null && a.active.every((x) => x === a.bidder)) {
    s.owner[a.sq] = a.bidder;
    s.players[a.bidder]!.cash -= a.bid;
    say(s, a.bidder, `køber ${nameOfSq(a.sq)} på auktion for ${a.bid}.`);
    s.auction = null;
    s.stage = "manage";
    return;
  }
  if (a.bidder === null && a.active.length === 0) {
    say(s, null, `Ingen ville købe ${nameOfSq(a.sq)}.`);
    s.auction = null;
    s.stage = "manage";
    return;
  }
  const pos = a.order.indexOf(from);
  for (let k = 1; k <= a.order.length; k++) {
    const cand = a.order[(pos + k) % a.order.length]!;
    if (a.active.includes(cand)) { a.cur = cand; return; }
  }
}

function checkWinner(s: MState) {
  const a = alive(s);
  if (a.length === 1) s.winner = a[0]!;
}

function finishByLimit(s: MState) {
  const a = alive(s).map((i) => ({ i, w: netWorth(s, i) })).sort((x, y) => y.w - x.w);
  s.winner = a.length > 1 && a[0]!.w === a[1]!.w ? "draw" : a[0]!.i;
  say(s, null, "Tiden er gået – den rigeste vinder.");
}

function nextPlayer(s: MState) {
  const a = alive(s);
  const i = a.findIndex((x) => x > s.turn);
  s.turn = a[i < 0 ? 0 : i]!;
  s.turnCount++;
  s.stage = "roll";
  s.rolled = false;
  s.doubles = 0;
  s.buildFailed = false;
  s.jailQuiz = false;
  if (s.turnCount >= s.limitTurns) finishByLimit(s);
}

function transferAll(s: MState, from: number, to: number | null) {
  const p = s.players[from]!;
  s.owner.forEach((o, i) => {
    if (o !== from) return;
    const q = SQUARES[i]!;
    if (q.t === "prop") { p.cash += Math.floor((s.houses[i]! * q.house) / 2); s.houses[i] = 0; }
    if (to === null) { s.owner[i] = null; s.mort[i] = false; }
    else {
      s.owner[i] = to;
      if (s.mort[i]) s.players[to]!.cash -= Math.min(s.players[to]!.cash, Math.ceil(priceOf(i) / 2 * 0.1));
    }
  });
  if (to !== null) { s.players[to]!.cash += p.cash; s.players[to]!.jailCards += p.jailCards; }
  p.cash = 0;
  p.jailCards = 0;
}

function validateTrade(s: MState, t: TradeOffer) {
  if (t.from === t.to || !s.players[t.to] || s.players[t.to]!.out) throw new GameError("Ugyldig medspiller.");
  const ok = (a: unknown) => Number.isInteger(a) && (a as number) >= 0 && (a as number) <= 100000;
  if (!ok(t.giveCash) || !ok(t.getCash)) throw new GameError("Ugyldigt beløb.");
  if (t.giveCash > s.players[t.from]!.cash) throw new GameError("Du har ikke så mange penge.");
  if (t.getCash > s.players[t.to]!.cash) throw new GameError("Modparten har ikke så mange penge.");
  if (new Set(t.giveProps).size !== t.giveProps.length || new Set(t.getProps).size !== t.getProps.length) throw new GameError("Ugyldig handel.");
  for (const i of t.giveProps) if (!Number.isInteger(i) || s.owner[i] !== t.from) throw new GameError("Du ejer ikke alle de ejendomme.");
  for (const i of t.getProps) if (!Number.isInteger(i) || s.owner[i] !== t.to) throw new GameError("Modparten ejer ikke alle de ejendomme.");
  if (!t.giveProps.length && !t.getProps.length && !t.giveCash && !t.getCash) throw new GameError("Tilbuddet er tomt.");
  for (const i of [...t.giveProps, ...t.getProps]) {
    const g = groupOf(i);
    if (g && GROUPS[g].some((x) => s.houses[x]! > 0)) throw new GameError("Bygninger skal sælges, før en ejendom i den farve kan handles.");
  }
}

function newMortgageFee(s: MState, props: number[]) {
  return props.filter((i) => s.mort[i]).reduce((a, i) => a + Math.ceil(priceOf(i) / 2 * 0.1), 0);
}

function requireManageStage(s: MState) {
  if (s.q) throw new GameError("Svar på spørgsmålet først.");
  if (!["roll", "manage", "debt", "buy"].includes(s.stage)) throw new GameError("Det kan du ikke lige nu.");
}

export interface MonopolyView {
  you: number;
  actor: number;
  turn: number;
  stage: Stage;
  players: (MPlayer & { worth: number })[];
  owner: (number | null)[];
  houses: number[];
  mort: boolean[];
  dice: [number, number];
  rolled: boolean;
  doubles: number;
  q: MQuestion | null;
  auction: Auction | null;
  trade: (TradeOffer & { back: Stage }) | null;
  debt: { seat: number; total: number; items: { to: number | null; amount: number }[] } | null;
  lastCard: MState["lastCard"];
  feed: FeedItem[];
  round: number;
  rounds: number;
  supply: { houses: number; hotels: number };
  options: ReturnType<typeof manageOptions>;
  buildFailed: boolean;
  jailQuiz: boolean;
  winner: Winner;
}

export const monopoly: TurnGameModule<MState, MAction, MonopolyView> & { resolve: NonNullable<TurnGameModule<MState, MAction, MonopolyView>["resolve"]> } = {
  id: "monopoly" as TurnGameModule<MState, MAction, MonopolyView>["id"],
  actionMs: 120_000,
  create(rng, players = 2, opts?: { rounds?: number }) {
    const rounds = opts?.rounds ?? 20;
    return {
      players: Array.from({ length: players }, () => ({ cash: START_CASH, pos: 0, jail: 0, jailCards: 0, out: false })),
      owner: Array(40).fill(null),
      houses: Array(40).fill(0),
      mort: Array(40).fill(false),
      turn: Math.floor(rng() * players),
      stage: "roll" as Stage,
      dice: [1, 1] as [number, number],
      rolled: false,
      doubles: 0,
      q: null,
      auction: null,
      trade: null,
      debt: null,
      pendingMove: null,
      buildFailed: false,
      jailQuiz: false,
      chanceDeck: shuffle(CHANCE.map((_, i) => i), rng),
      chestDeck: shuffle(CHEST.map((_, i) => i), rng),
      lastCard: null,
      feed: [],
      turnCount: 0,
      limitTurns: rounds * players,
      winner: null,
    };
  },
  phase: (s) => (s.winner !== null ? "over" : "play"),
  turn: actorOf,
  setupSeats: () => [],
  needsQuestion: (s) => (s.q ? s.q.kind : null),

  apply(s, seat, a, rng) {
    if (s.winner !== null) throw new GameError("Spillet er slut.");
    if (!a || typeof a !== "object") throw new GameError("Ukendt handling.");
    if (seat !== actorOf(s)) throw new GameError("Det er ikke din tur.");
    if (s.q) throw new GameError("Svar på spørgsmålet først.");
    const me = s.players[seat]!;
    const ev: GameEvent[] = [];
    const idx = (a as { sq?: number }).sq;
    const needSq = () => { if (!Number.isInteger(idx) || idx! < 0 || idx! > 39) throw new GameError("Ugyldigt felt."); return idx!; };

    switch (a.type) {
      case "roll": {
        if (s.stage !== "roll" || s.rolled) throw new GameError("Du kan ikke slå nu.");
        const d1 = roll6(rng), d2 = roll6(rng);
        s.dice = [d1, d2];
        s.rolled = true;
        const dbl = d1 === d2;
        say(s, seat, `slår ${d1} og ${d2}.`);
        if (me.jail > 0) {
          if (dbl) {
            me.jail = 0;
            s.doubles = 0;
            say(s, seat, "slog to ens og slipper ud af fængslet.");
          } else {
            me.jail++;
            s.doubles = 0;
            if (me.jail <= 3) {
              say(s, seat, "slog ikke to ens og bliver i fængslet.");
              s.stage = "manage";
              return ev;
            }
            me.jail = 0;
            say(s, seat, `har siddet inde tre gange og betaler ${JAIL_FINE}.`);
            s.pendingMove = d1 + d2;
            charge(s, seat, JAIL_FINE, null);
            if ((s.stage as Stage) === "debt") return ev;
            s.pendingMove = null;
          }
        } else if (dbl) {
          s.doubles++;
          if (s.doubles >= 3) {
            say(s, seat, "slog tre ens i træk.");
            sendToJail(s, seat);
            s.stage = "manage";
            return ev;
          }
        } else s.doubles = 0;
        const steps = d1 + d2;
        const before = me.pos;
        me.pos = (before + steps) % 40;
        if (me.pos < before) passGo(s, seat);
        land(s, seat, rng);
        return ev;
      }
      case "buy": {
        if (s.stage !== "buy") throw new GameError("Der er intet at købe.");
        const price = priceOf(me.pos);
        if (me.cash < price) throw new GameError("Du har ikke penge nok. Pantsæt noget eller lad den gå på auktion.");
        me.cash -= price;
        s.owner[me.pos] = seat;
        s.stage = "manage";
        say(s, seat, `køber ${nameOfSq(me.pos)} for ${price}.`);
        return ev;
      }
      case "decline": {
        if (s.stage !== "buy") throw new GameError("Der er intet at afslå.");
        startAuction(s, me.pos, seat);
        return ev;
      }
      case "end": {
        if (s.stage !== "manage") throw new GameError("Du kan ikke afslutte turen nu.");
        if (s.doubles > 0 && me.jail === 0) {
          s.stage = "roll";
          s.rolled = false;
          say(s, seat, "slog to ens og slår igen.");
          return ev;
        }
        nextPlayer(s);
        return ev;
      }
      case "payjail": {
        if (s.stage !== "roll" || me.jail === 0 || s.rolled) throw new GameError("Du er ikke i fængsel.");
        if (me.cash < JAIL_FINE) throw new GameError("Du har ikke penge til bøden.");
        me.cash -= JAIL_FINE;
        me.jail = 0;
        say(s, seat, `betaler ${JAIL_FINE} og er fri.`);
        return ev;
      }
      case "usecard": {
        if (s.stage !== "roll" || me.jail === 0 || s.rolled) throw new GameError("Du er ikke i fængsel.");
        if (me.jailCards < 1) throw new GameError("Du har intet løsladelseskort.");
        me.jailCards--;
        me.jail = 0;
        if (!s.chanceDeck.includes(7)) s.chanceDeck.push(7);
        else s.chestDeck.push(4);
        say(s, seat, "bruger et løsladelseskort og er fri.");
        return ev;
      }
      case "jailquiz": {
        if (s.stage !== "roll" || me.jail === 0 || s.rolled) throw new GameError("Du er ikke i fængsel.");
        if (s.jailQuiz) throw new GameError("Du har allerede forsøgt i denne tur.");
        s.jailQuiz = true;
        s.q = { kind: "jail", seat, sq: 10 };
        return ev;
      }
      case "build": {
        requireManageStage(s);
        if (s.stage === "debt") throw new GameError("Betal først din gæld.");
        const i = needSq();
        if (!manageOptions(s, seat).build.includes(i)) throw new GameError("Du kan ikke bygge dér lige nu (hele farven skal ejes, ingen pantsat, byg jævnt, og du skal have råd).");
        s.q = { kind: "build", seat, sq: i };
        return ev;
      }
      case "sell": {
        requireManageStage(s);
        const i = needSq();
        if (!manageOptions(s, seat).sell.includes(i)) throw new GameError("Du kan ikke sælge dér (sælg jævnt).");
        const q = SQUARES[i] as Extract<Sq, { t: "prop" }>;
        s.houses[i] = s.houses[i] === 5 ? 4 : s.houses[i]! - 1;
        me.cash += Math.floor(q.house / 2);
        say(s, seat, `sælger en bygning på ${q.name}.`);
        return ev;
      }
      case "mortgage": {
        requireManageStage(s);
        const i = needSq();
        if (!manageOptions(s, seat).mortgage.includes(i)) throw new GameError("Du kan ikke pantsætte den (sælg først bygninger i farven).");
        s.mort[i] = true;
        me.cash += priceOf(i) / 2;
        say(s, seat, `pantsætter ${nameOfSq(i)}.`);
        return ev;
      }
      case "unmortgage": {
        requireManageStage(s);
        if (s.stage === "debt") throw new GameError("Betal først din gæld.");
        const i = needSq();
        if (!manageOptions(s, seat).unmortgage.includes(i)) throw new GameError("Du kan ikke indløse den (mangler penge).");
        s.mort[i] = false;
        me.cash -= Math.ceil(priceOf(i) / 2 * 1.1);
        say(s, seat, `indløser ${nameOfSq(i)}.`);
        return ev;
      }
      case "paydebt": {
        if (s.stage !== "debt" || !s.debt) throw new GameError("Du skylder ikke noget.");
        const total = s.debt.items.reduce((x, y) => x + y.amount, 0);
        if (me.cash < total) throw new GameError("Du har ikke penge nok endnu. Sælg bygninger, pantsæt ejendomme eller erklær dig konkurs.");
        for (const it of s.debt.items) { me.cash -= it.amount; if (it.to !== null) s.players[it.to]!.cash += it.amount; }
        s.debt = null;
        s.stage = "manage";
        if (s.pendingMove !== null) {
          const steps = s.pendingMove;
          s.pendingMove = null;
          const before = me.pos;
          me.pos = (before + steps) % 40;
          if (me.pos < before) passGo(s, seat);
          land(s, seat, rng);
        }
        return ev;
      }
      case "bankrupt": {
        if (s.stage !== "debt" || !s.debt) throw new GameError("Du kan kun gå konkurs, når du skylder penge.");
        const creditor = s.debt.items[0]!.to;
        say(s, seat, creditor === null ? "går konkurs til banken." : "går konkurs.");
        transferAll(s, seat, creditor);
        me.out = true;
        s.debt = null;
        s.pendingMove = null;
        checkWinner(s);
        if (s.winner === null) nextPlayer(s);
        return ev;
      }
      case "bid": {
        if (s.stage !== "auction" || !s.auction) throw new GameError("Der er ingen auktion.");
        const au = s.auction;
        if (!Number.isInteger(a.amount) || a.amount <= au.bid) throw new GameError(`Du skal byde mere end ${au.bid}.`);
        if (a.amount > me.cash) throw new GameError("Du har ikke så mange penge.");
        au.bid = a.amount;
        au.bidder = seat;
        say(s, seat, `byder ${a.amount}.`);
        advanceAuction(s, seat);
        return ev;
      }
      case "pass": {
        if (s.stage !== "auction" || !s.auction) throw new GameError("Der er ingen auktion.");
        const au = s.auction;
        if (au.bidder === seat) throw new GameError("Du har det højeste bud.");
        au.active = au.active.filter((x) => x !== seat);
        say(s, seat, "byder ikke mere.");
        advanceAuction(s, seat);
        return ev;
      }
      case "propose": {
        requireManageStage(s);
        if (s.stage === "debt" || s.stage === "buy") throw new GameError("Afslut først det, du er i gang med.");
        const t: TradeOffer = { from: seat, to: a.to, giveProps: [...(a.giveProps ?? [])], giveCash: a.giveCash ?? 0, getProps: [...(a.getProps ?? [])], getCash: a.getCash ?? 0 };
        validateTrade(s, t);
        s.trade = { ...t, back: s.stage };
        s.stage = "trade";
        say(s, seat, "foreslår en handel.");
        return ev;
      }
      case "accept": {
        if (s.stage !== "trade" || !s.trade) throw new GameError("Der er ingen handel.");
        const t = s.trade;
        validateTrade(s, t);
        const feeMe = newMortgageFee(s, t.giveProps); // paid by the receiver (t.to)
        const feeThem = newMortgageFee(s, t.getProps); // paid by the proposer (t.from)
        const toP = s.players[t.to]!, fromP = s.players[t.from]!;
        if (toP.cash + t.giveCash - t.getCash < feeMe || fromP.cash - t.giveCash + t.getCash < feeThem) throw new GameError("Der mangler penge til renten på pantsatte ejendomme.");
        for (const i of t.giveProps) s.owner[i] = t.to;
        for (const i of t.getProps) s.owner[i] = t.from;
        fromP.cash += t.getCash - t.giveCash - feeThem;
        toP.cash += t.giveCash - t.getCash - feeMe;
        s.trade = null;
        s.stage = t.back;
        say(s, seat, "accepterer handlen.");
        return ev;
      }
      case "reject": {
        if (s.stage !== "trade" || !s.trade) throw new GameError("Der er ingen handel.");
        s.stage = s.trade.back;
        s.trade = null;
        say(s, seat, "afslår handlen.");
        return ev;
      }
    }
    throw new GameError("Ukendt handling.");
  },

  /** Called by the engine after the question for `s.q` was answered (or timed out). */
  resolve(s, seat, correct, rng) {
    const q = s.q;
    s.q = null;
    if (!q || q.seat !== seat) return [];
    const me = s.players[seat]!;
    switch (q.kind) {
      case "buy":
        if (correct) { s.stage = "buy"; say(s, seat, `svarede rigtigt og må købe ${nameOfSq(q.sq)}.`); }
        else { say(s, seat, "svarede forkert og mister købsretten."); startAuction(s, q.sq, seat); }
        break;
      case "rent":
      case "tax": {
        const full = q.amount ?? 0;
        const amount = correct ? Math.ceil(full / 2) : full;
        s.stage = "manage";
        say(s, seat, correct ? `svarede rigtigt og betaler kun halv ${q.kind === "rent" ? "leje" : "skat"}: ${amount}.` : `svarede forkert og betaler fuld ${q.kind === "rent" ? "leje" : "skat"}: ${amount}.`);
        charge(s, seat, amount, q.to ?? null);
        break;
      }
      case "build": {
        if (!correct) { s.buildFailed = true; say(s, seat, "svarede forkert – der bygges ikke mere i denne tur."); break; }
        if (!manageOptions(s, seat).build.includes(q.sq)) break;
        const sqd = SQUARES[q.sq] as Extract<Sq, { t: "prop" }>;
        me.cash -= sqd.house;
        s.houses[q.sq] = s.houses[q.sq]! + 1;
        say(s, seat, s.houses[q.sq] === 5 ? `bygger et hotel på ${sqd.name}.` : `bygger et hus på ${sqd.name}.`);
        break;
      }
      case "jail":
        if (correct) { me.jail = 0; say(s, seat, "svarede rigtigt og slipper gratis ud af fængslet."); }
        else say(s, seat, "svarede forkert og bliver i fængslet.");
        break;
    }
    void rng;
    return [];
  },
  penalty(s, seat, rng) { return monopoly.resolve(s, seat, false, rng); },

  /** Timeouts: make the safest automatic choice so the game never stalls. */
  pass(s, seat) {
    if (s.winner !== null) return [];
    const rng = () => 0.5;
    try {
      switch (s.stage) {
        case "roll": return s.rolled ? [] : monopoly.apply(s, seat, { type: "roll" }, Math.random);
        case "buy": return monopoly.apply(s, seat, { type: "decline" }, rng);
        case "manage": return monopoly.apply(s, seat, { type: "end" }, rng);
        case "auction": return monopoly.apply(s, seat, { type: "pass" }, rng);
        case "trade": return monopoly.apply(s, seat, { type: "reject" }, rng);
        case "debt": {
          try { return monopoly.apply(s, seat, { type: "paydebt" }, Math.random); } catch { return monopoly.apply(s, seat, { type: "bankrupt" }, rng); }
        }
      }
    } catch { /* fall through */ }
    return [];
  },
  continues: () => false,
  winner: (s) => s.winner,

  view(s, seat): MonopolyView {
    const act = actorOf(s);
    const aliveN = alive(s).length || 1;
    return {
      you: seat,
      actor: act,
      turn: s.turn,
      stage: s.stage,
      players: s.players.map((p, i) => ({ ...p, worth: netWorth(s, i) })),
      owner: s.owner.slice(),
      houses: s.houses.slice(),
      mort: s.mort.slice(),
      dice: s.dice,
      rolled: s.rolled,
      doubles: s.doubles,
      q: s.q,
      auction: s.auction,
      trade: s.trade,
      debt: s.debt ? { seat: s.debt.seat, total: s.debt.items.reduce((a, i) => a + i.amount, 0), items: s.debt.items } : null,
      lastCard: s.lastCard,
      feed: s.feed.slice(-12),
      round: Math.min(Math.floor(s.turnCount / aliveN) + 1, Math.ceil(s.limitTurns / s.players.length)),
      rounds: Math.ceil(s.limitTurns / s.players.length),
      supply: { houses: houseSupplyLeft(s), hotels: hotelSupplyLeft(s) },
      options: seat === s.turn && !s.q && ["roll", "manage", "debt", "buy"].includes(s.stage) ? manageOptions(s, seat) : { build: [], sell: [], mortgage: [], unmortgage: [] },
      buildFailed: s.buildFailed,
      jailQuiz: s.jailQuiz,
      winner: s.winner,
    };
  },

  bot(v, seat, level: BotLevel, rng): MAction {
    const me = v.players[seat]!;
    const reserve = level === "easy" ? 0 : level === "medium" ? 100 : 150;
    const valueOf = (sq: number) => {
      const q = SQUARES[sq]!;
      let val = priceOf(sq);
      if (q.t === "prop") {
        const g = GROUPS[q.group];
        const mine = g.filter((x) => v.owner[x] === seat).length;
        if (mine === g.length - 1 && g.every((x) => v.owner[x] === seat || v.owner[x] === null)) val *= 1.5;
        else if (mine > 0) val *= 1.15;
      } else if (q.t === "rail") val *= 1 + 0.2 * RAILS.filter((x) => v.owner[x] === seat).length;
      return val;
    };
    if (v.stage === "auction" && v.auction) {
      const au = v.auction;
      const cap = Math.min(me.cash - 30, valueOf(au.sq) * (level === "easy" ? 0.7 : level === "medium" ? 0.95 : 1.1));
      const next = au.bid + (au.bid < 100 ? 10 : 20);
      if (au.bidder !== seat && next <= cap && rng() < 0.9) return { type: "bid", amount: next };
      return { type: "pass" };
    }
    if (v.stage === "trade" && v.trade) {
      const t = v.trade;
      const got = t.giveProps.reduce((a, i) => a + valueOf(i), 0) + t.giveCash;
      const given = t.getProps.reduce((a, i) => a + valueOf(i) * 1.25, 0) + t.getCash;
      return got >= given && got > 0 ? { type: "accept" } : { type: "reject" };
    }
    if (v.stage === "debt" && v.debt) {
      if (me.cash >= v.debt.total) return { type: "paydebt" };
      if (v.options.sell.length) return { type: "sell", sq: v.options.sell[0]! };
      const m = [...v.options.mortgage].sort((x, y) => priceOf(y) - priceOf(x))[0];
      if (m !== undefined) return { type: "mortgage", sq: m };
      return { type: "bankrupt" };
    }
    if (v.stage === "buy") {
      const sq = me.pos;
      const price = priceOf(sq);
      const completes = SQUARES[sq]!.t === "prop" && valueOf(sq) > price * 1.4;
      if (me.cash - price >= (completes ? 0 : reserve) && (level !== "easy" || rng() < 0.9)) return { type: "buy" };
      return { type: "decline" };
    }
    if (v.stage === "roll") {
      if (me.jail > 0 && !v.rolled) {
        if (me.jailCards > 0) return { type: "usecard" };
        if (me.cash > 400 && v.round < v.rounds - 2) return { type: "payjail" };
      }
      if (level !== "easy" && v.options.unmortgage.length && me.cash > 700) return { type: "unmortgage", sq: v.options.unmortgage[0]! };
      return { type: "roll" };
    }
    // manage
    if (level !== "easy" && v.options.build.length) {
      const best = [...v.options.build].sort((x, y) => (SQUARES[y] as { house: number }).house - (SQUARES[x] as { house: number }).house)[0]!;
      const cost = (SQUARES[best] as { house: number }).house;
      if (me.cash - cost >= (level === "hard" ? 150 : 300)) return { type: "build", sq: best };
    }
    if (v.options.unmortgage.length && me.cash > 800) return { type: "unmortgage", sq: v.options.unmortgage[0]! };
    return { type: "end" };
  },
};
